import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Radar, Target, RefreshCw, CheckCircle2, XCircle, Clock,
  Building2, MapPin, DollarSign, ExternalLink, ChevronRight,
  Search, Zap, Trophy, Medal, Award, Loader2, Calendar,
  FileText, User, Globe, FolderUp, AlertCircle, SlidersHorizontal,
  X, Plus, ChevronsUpDown, ChevronUp, ChevronDown,
  OctagonX, CircleCheck, Brain, Trash2, Link as LinkIcon,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { AI_RANK_ORDER } from '../../lib/aiRank';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import EbuyTab from '../../components/sentinel/EbuyTab';
import HigherGovTab from '../../components/sentinel/HigherGovTab';
import AiRankThresholds from '../../components/sentinel/AiRankThresholds';

// ── Set-aside options ────────────────────────────────────────────────────────
const SET_ASIDE_OPTIONS = [
  { code: 'SBA',      label: 'Small Business' },
  { code: 'SBP',      label: 'Partial SB' },
  { code: '8A',       label: '8(a) Set-Aside' },
  { code: '8AN',      label: '8(a) Sole Source' },
  { code: 'HZC',      label: 'HUBZone' },
  { code: 'WOSB',     label: 'WOSB' },
  { code: 'WOSBSS',   label: 'WOSB Sole Source' },
  { code: 'EDWOSB',   label: 'EDWOSB' },
  { code: 'EDWOSBSS', label: 'EDWOSB Sole Source' },
  { code: 'SDVOSBC',  label: 'SDVOSB' },
  { code: 'VSB',      label: 'Veteran-Owned' },
  { code: 'NONE',     label: 'Unrestricted' },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MATCH_TIERS = {
  gold:   { label: 'Gold',   icon: Trophy, iconBg: 'bg-amber-100 text-amber-600',   badgeClass: 'bg-amber-100 text-amber-700 border border-amber-200' },
  silver: { label: 'Silver', icon: Medal,  iconBg: 'bg-slate-200 text-slate-700',   badgeClass: 'bg-slate-200 text-slate-700 border border-slate-400' },
  bronze: { label: 'Bronze', icon: Award,  iconBg: 'bg-orange-100 text-orange-600', badgeClass: 'bg-orange-100 text-orange-700 border border-orange-200' },
};

const STATUS_CONFIG = {
  suggested:   { label: 'New',         color: 'bg-blue-100 text-blue-700' },
  claimed:     { label: 'Claimed',     color: 'bg-emerald-100 text-emerald-700' },
  recommended: { label: 'Recommended', color: 'bg-purple-100 text-purple-700' },
  dismissed:   { label: 'Dismissed',   color: 'bg-slate-100 text-slate-500' },
};

// AI fit-ranking color scale
const AI_RANK_CONFIG = {
  BLUE:   { label: 'Blue',   cls: 'bg-blue-600 text-white',          dot: 'bg-blue-600',   tip: '≥50% requirements met — strong fit'    },
  GREEN:  { label: 'Green',  cls: 'bg-green-600 text-white',         dot: 'bg-green-600',  tip: '≥40% requirements met — good fit'       },
  YELLOW: { label: 'Yellow', cls: 'bg-yellow-500 text-white',        dot: 'bg-yellow-500', tip: '≥30% requirements met — moderate fit'   },
  RED:    { label: 'Red',    cls: 'bg-red-600 text-white',           dot: 'bg-red-600',    tip: '≥20% requirements met — weak fit'       },
  GRAY:   { label: 'Gray',   cls: 'bg-slate-400 text-white',         dot: 'bg-slate-400',  tip: '<20% requirements met — poor fit'       },
};

function toApiDate(d) {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${m}/${day}/${d.getFullYear()}`;
}
function toInputDate(d) { return d.toISOString().slice(0, 10); }

// ─────────────────────────────────────────────────────────────────────────────
export default function MarketplaceSentinel() {
  const { authToken, user } = useAuth();
  const navigate = useNavigate();

  const [opportunities, setOpportunities] = useState([]);
  const [samPage, setSamPage] = useState(1);     // SAM list: current loaded page
  const [samTotal, setSamTotal] = useState(0);   // SAM list: total matching server-side
  const [samLoadingMore, setSamLoadingMore] = useState(false);
  const SAM_PAGE_SIZE = 50;
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [rankingSam, setRankingSam] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [filterStatus, setFilterStatus] = useState('suggested');
  // Server-side: the list is paginated, so filtering here would only ever
  // narrow the page you happen to be looking at.
  const [filterAiRank, setFilterAiRank] = useState('all');
  const [showRankBands, setShowRankBands] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [showScanOptions, setShowScanOptions] = useState(false);

  const isConsultant = user?.role === 'CONSULTANT' || user?.role === 'CONSULTANT_ADMIN';
  const [clientOptions, setClientOptions] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  // Consultants must pick a specific client before scanning/syncing.
  const needsClient = isConsultant && !selectedClientId;

  // Brand colors fetched fresh whenever the selected client changes.
  // Using dedicated state (not a derived value from clientOptions) so we
  // always reflect the DB at the moment of selection regardless of whether
  // the list data was stale or loaded before the scan ran.
  const [activeBrandColors, setActiveBrandColors] = useState(null);

  useEffect(() => {
    if (!selectedClientId || !authToken) {
      setActiveBrandColors(null);
      return;
    }
    // Optimistic: use whatever is in clientOptions right now
    const cached = clientOptions.find(c => c.id === selectedClientId)?.brand_colors || null;
    setActiveBrandColors(cached);
    // Always fetch fresh from the server to catch post-scan updates
    fetch(`${API_URL}/api/clients/${selectedClientId}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const colors = data?.brand_colors || null;
        setActiveBrandColors(colors);
        // Keep clientOptions cache in sync
        if (colors) {
          setClientOptions(prev =>
            prev.map(c => c.id === selectedClientId ? { ...c, brand_colors: colors } : c)
          );
        }
      })
      .catch(() => { /* non-fatal — optimistic value stays */ });
  }, [selectedClientId, authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // CSS custom properties injected on the root element when a branded client is selected
  const clientThemeVars = activeBrandColors?.primary ? {
    '--client-primary':    activeBrandColors.primary,
    '--client-secondary':  activeBrandColors.secondary  || activeBrandColors.primary,
    '--client-accent':     activeBrandColors.accent     || activeBrandColors.primary,
    '--client-bg':         activeBrandColors.background || '#f8fafc',
    '--client-text-color': activeBrandColors.text       || '#f8fafc',
  } : {};

  const defaultRdlFrom = toInputDate(new Date());
  const defaultRdlTo   = toInputDate(new Date(Date.now() + 90 * 86400000));
  const [scanRdlFrom, setScanRdlFrom] = useState(defaultRdlFrom);
  const [scanRdlTo,   setScanRdlTo]   = useState(defaultRdlTo);

  // Refresh the scan window to "today → +90d" whenever the selected client
  // changes (and on mount). Prevents a stale 'from' date (frozen at mount on
  // a long-open tab) from carrying over and scanning already-passed deadlines.
  useEffect(() => {
    setScanRdlFrom(toInputDate(new Date()));
    setScanRdlTo(toInputDate(new Date(Date.now() + 90 * 86400000)));
    // Close the open detail panel so it doesn't linger on the previous
    // client's opportunity; the refetch then auto-opens the new first opp.
    setSelectedOpp(null);
  }, [selectedClientId]);
  const [scanNaics,   setScanNaics]   = useState([]);
  const [naicsInput,  setNaicsInput]  = useState('');
  const [scanSetAsides, setScanSetAsides] = useState([]);

  const [activeFeed, setActiveFeed] = useState('sam');

  // ── SAM table sort state ──────────────────────────────────────────────────
  const [samSortField, setSamSortField] = useState('response_deadline');
  const [samSortDir,   setSamSortDir]   = useState('asc');

  const ebuyTabRef = useRef(null);
  const [ebuySyncing, setEbuySyncing] = useState(false);
  const [ebuyLatestSync, setEbuyLatestSync] = useState(null);

  const hgTabRef = useRef(null);
  const [hgSyncing, setHgSyncing] = useState(false);
  const [hgLatestSync, setHgLatestSync] = useState(null);
  const [hgStats, setHgStats] = useState({});

  // Purge old unclaimed opps
  const [showPurgeDialog, setShowPurgeDialog] = useState(false); // 'sam' | 'ebuy' | false
  const [purgeDays, setPurgeDays] = useState(30);
  const [purging, setPurging] = useState(false);

  const [showManualAddDialog, setShowManualAddDialog] = useState(false);
  const [manualAddInput, setManualAddInput] = useState('');
  const [manualAdding, setManualAdding] = useState(false);

  const fetchOpportunities = useCallback(async (page = 1, append = false) => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterTier !== 'all') params.append('tier', filterTier);
      if (filterAiRank !== 'all') params.append('ai_rank', filterAiRank);
      if (selectedClientId) params.append('organization_id', selectedClientId);
      params.append('page', String(page));
      params.append('limit', String(SAM_PAGE_SIZE));
      const response = await fetch(`${API_URL}/api/sentinel/opportunities?${params}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        const opps = data.opportunities || [];
        setSamTotal(data.total ?? opps.length);
        setSamPage(page);
        // append = "Load more" pages onto the loaded set; otherwise replace (page 1)
        setOpportunities(prev => append ? [...prev, ...opps] : opps);
        if (!append && opps.length > 0) {
          setSelectedOpp(prev => prev || opps[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch opportunities:', error);
    } finally {
      setLoading(false);
    }
  }, [authToken, filterStatus, filterTier, filterAiRank, selectedClientId]);

  // "Load more" — pull the next server page and append (lets you page through
  // days of accumulated matches without losing what's already loaded).
  const loadMoreSam = async () => {
    setSamLoadingMore(true);
    await fetchOpportunities(samPage + 1, true);
    setSamLoadingMore(false);
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/sentinel/stats`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [authToken]);

  const fetchHgStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedClientId) params.append('organization_id', selectedClientId);
      const res = await fetch(`${API_URL}/api/highergov/stats?${params}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) setHgStats(await res.json());
    } catch { /* non-fatal */ }
  }, [authToken, selectedClientId]);

  const fetchIntegrationStatus = useCallback(async () => {
    try {
      // Scope to the selected client so the status (incl. last_scan_result)
      // reflects the org actually being scanned, not the caller's own org.
      const url = isConsultant && selectedClientId
        ? `${API_URL}/api/sentinel/integrations/sam-gov?organization_id=${selectedClientId}`
        : `${API_URL}/api/sentinel/integrations/sam-gov`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setIntegrationStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch integration status:', error);
    }
  }, [authToken, isConsultant, selectedClientId]);

  const handleScan = async () => {
    setScanning(true);
    const scanParamsBody = {};
    if (scanRdlFrom) scanParamsBody.rdl_from = toApiDate(new Date(scanRdlFrom + 'T00:00:00'));
    if (scanRdlTo)   scanParamsBody.rdl_to   = toApiDate(new Date(scanRdlTo   + 'T00:00:00'));
    scanParamsBody.naics_codes = scanNaics;
    if (scanSetAsides.length > 0) scanParamsBody.set_aside_codes = scanSetAsides;

    const endpoint = isConsultant && selectedClientId
      ? `${API_URL}/api/clients/${selectedClientId}/sentinel-scan`
      : `${API_URL}/api/sentinel/scan`;
    const clientName = clientOptions.find(c => c.id === selectedClientId)?.name;
    toast.info(isConsultant && selectedClientId
      ? `Scanning SAM.gov for ${clientName || 'client'}...`
      : 'Scanning SAM.gov for new opportunities...', { duration: 3000 });

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(scanParamsBody),
      });
      if (response.ok) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        await fetchOpportunities();
        await fetchStats();
        const statusUrl = isConsultant && selectedClientId
          ? `${API_URL}/api/sentinel/integrations/sam-gov?organization_id=${selectedClientId}`
          : `${API_URL}/api/sentinel/integrations/sam-gov`;
        const statusRes = await fetch(statusUrl, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        const status = statusRes.ok ? await statusRes.json() : null;
        const result = status?.last_scan_result;
        if (result?.error) {
          toast.error(`Scan failed: ${result.error.detail || 'Unknown error'}`, { duration: 10000 });
        } else if (result?.checked_window) {
          const w = result.checked_window;
          const newM = result.new_matches || 0;
          const total = result.total_records || 0;
          const fetched = result.fetched || 0;
          const dlFrom = w.rdl_from ? `${w.rdl_from} – ` : '';
          const dlTo = w.rdl_to || w.posted_to;
          const deadlineRange = `response deadline ${dlFrom}${dlTo}`;
          const naicsLabel = w.all_naics_mode ? 'All NAICS' : (w.naics_codes?.join(', ') || w.ncode || '');
          const saLabel = w.type_of_set_aside?.length ? ` · set-aside: ${w.type_of_set_aside.join(', ')}` : '';
          if (total === 0) {
            toast.info(`Scan complete — 0 opportunities on SAM.gov for ${deadlineRange}${naicsLabel ? ` · ${naicsLabel}` : ''}${saLabel}.`, { duration: 8000 });
          } else if (newM === 0) {
            toast.success(`Scan complete — ${fetched.toLocaleString()} of ${total.toLocaleString()} fetched (${deadlineRange}${naicsLabel ? ` · ${naicsLabel}` : ''}${saLabel}); 0 new matches.`, { duration: 8000 });
          } else {
            toast.success(`${newM} new match${newM === 1 ? '' : 'es'} — ${fetched.toLocaleString()} of ${total.toLocaleString()} fetched (${deadlineRange}${naicsLabel ? ` · ${naicsLabel}` : ''}${saLabel}).`, { duration: 8000 });
          }
        } else {
          toast.success('Scan complete! Check for new matches.');
        }
        if (status) setIntegrationStatus(status);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Scan failed');
      }
    } catch (error) {
      toast.error('Scan failed - please try again');
    } finally {
      setScanning(false);
    }
  };

  const fetchClients = useCallback(async () => {
    if (!isConsultant || !user?.organizationId) return;
    try {
      const res = await fetch(
        `${API_URL}/api/clients?consultant_org_id=${encodeURIComponent(user.organizationId)}&page_size=100`,
        { headers: { 'Authorization': `Bearer ${authToken}` } },
      );
      if (!res.ok) return;
      const body = await res.json();
      setClientOptions((body.items || []).map(c => ({ id: c.id, name: c.name, brand_colors: c.brand_colors || null })));
    } catch (e) {
      console.error('Failed to load clients for picker:', e);
    }
  }, [authToken, isConsultant, user]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // ── Purge old unclaimed opportunities ──────────────────────────────────
  const handlePurge = async () => {
    if (!showPurgeDialog) return;
    const orgId = selectedClientId || user?.organization_id;
    if (!orgId) return;
    setPurging(true);
    try {
      const feed = showPurgeDialog; // 'sam' | 'ebuy' | 'highergov'
      const urlMap = {
        sam:       `${API_URL}/api/sentinel/opportunities/purge-old`,
        ebuy:      `${API_URL}/api/ebuy/opportunities/purge-old`,
        highergov: `${API_URL}/api/highergov/opportunities/purge-old`,
      };
      const labelMap = { sam: 'SAM', ebuy: 'eBuy', highergov: 'HigherGov' };
      const params = new URLSearchParams({ older_than_days: String(purgeDays) });
      if (isConsultant && orgId) params.append('organization_id', orgId);
      const res = await fetch(`${urlMap[feed]}?${params}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      toast.success(`Deleted ${data.deleted} unclaimed ${labelMap[feed]} opportunit${data.deleted === 1 ? 'y' : 'ies'} older than ${purgeDays} days`);
      setShowPurgeDialog(false);
      if (feed === 'sam') fetchOpportunities();
      else if (feed === 'ebuy') ebuyTabRef.current?.refresh?.();
      else fetchHgStats();
    } catch (e) {
      toast.error(`Purge failed: ${e.message}`);
    } finally {
      setPurging(false);
    }
  };

  const handleManualAdd = async () => {
    if (!manualAddInput.trim()) return;
    setManualAdding(true);
    try {
      const endpoint = isConsultant && selectedClientId
        ? `${API_URL}/api/clients/${selectedClientId}/sentinel-manual-add`
        : `${API_URL}/api/sentinel/opportunities/manual-add`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url_or_notice_id: manualAddInput.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || `Couldn't add that opportunity (HTTP ${res.status})`, { duration: 8000 });
        return;
      }
      if (data.already_tracked) {
        toast.info(`Already tracked: "${data.opportunity?.title || 'that opportunity'}"`);
      } else {
        toast.success(`Added: "${data.opportunity?.title || 'opportunity'}"`, { duration: 6000 });
      }
      setShowManualAddDialog(false);
      setManualAddInput('');
      await fetchOpportunities();
      await fetchStats();
    } catch (e) {
      toast.error(`Add failed: ${e.message}`);
    } finally {
      setManualAdding(false);
    }
  };

  // ── AI Rank: SAM opps ───────────────────────────────────────────────────
  const handleRankSam = async () => {
    const orgId = selectedClientId || user?.organization_id;
    if (!orgId) return;
    setRankingSam(true);
    try {
      const res = await fetch(`${API_URL}/api/sentinel/opportunities/ai-rank`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ organization_id: orgId }),
      });
      const data = await res.json();
      if (res.ok) {
        // A partial run is the normal case when the backlog exceeds one batch —
        // give it a longer read so "N still unranked" isn't missed.
        toast.success(data.message || `Ranked ${data.ranked} opportunities`, {
          duration: data.remaining ? 10000 : 5000,
        });
        fetchOpportunities(); // refresh to show new ranks
      } else {
        toast.error(data.detail || 'Ranking failed');
      }
    } catch (e) {
      toast.error('Ranking request failed');
    } finally {
      setRankingSam(false);
    }
  };

  const handleClaim = async (oppId) => {
    try {
      const response = await fetch(`${API_URL}/api/sentinel/opportunities/${oppId}/claim`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success('Opportunity claimed! Redirecting to pursuit...');
        fetchOpportunities();
        fetchStats();
        navigate(`/${user.workspaceId}/pursuit/${data.pursuit_id}/intake`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to claim opportunity');
      }
    } catch (error) {
      toast.error('Failed to claim opportunity');
    }
  };

  const handleDismiss = async (oppId) => {
    try {
      const response = await fetch(`${API_URL}/api/sentinel/opportunities/${oppId}/dismiss`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        toast.success('Opportunity dismissed');
        fetchOpportunities();
        fetchStats();
        setSelectedOpp(null);
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to dismiss opportunity');
      }
    } catch (error) {
      toast.error('Failed to dismiss');
    }
  };

  useEffect(() => {
    fetchOpportunities();
    fetchStats();
    fetchIntegrationStatus();
    fetchHgStats();
  }, [fetchOpportunities, fetchStats, fetchIntegrationStatus, fetchHgStats]);

  const filteredOpportunities = (() => {
    const filtered = opportunities.filter(opp =>
      !searchQuery ||
      opp.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      opp.agency?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      opp.solicitation_number?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const TIER_ORDER = { gold: 0, silver: 1, bronze: 2 };
    return [...filtered].sort((a, b) => {
      let av = a[samSortField], bv = b[samSortField];
      if (samSortField === 'match_tier') { av = TIER_ORDER[av] ?? 99; bv = TIER_ORDER[bv] ?? 99; }
      else if (samSortField === 'ai_rank') { av = AI_RANK_ORDER[av] ?? 99; bv = AI_RANK_ORDER[bv] ?? 99; }
      else if (samSortField === 'response_deadline') { av = av ? new Date(av).getTime() : Infinity; bv = bv ? new Date(bv).getTime() : Infinity; }
      else if (samSortField === 'estimated_value') { av = av ?? -1; bv = bv ?? -1; }
      else { av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase(); }
      if (av < bv) return samSortDir === 'asc' ? -1 : 1;
      if (av > bv) return samSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  })();

  const toggleSamSort = (field) => {
    if (samSortField === field) setSamSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSamSortField(field); setSamSortDir('asc'); }
  };

  const SamSortIcon = ({ field }) => {
    if (samSortField !== field) return <ChevronsUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return samSortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 ml-1 text-teal-600" />
      : <ChevronDown className="w-3 h-3 ml-1 text-teal-600" />;
  };

  const formatCurrency = (value) => {
    if (!value) return 'TBD';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatLocation = (pop) => {
    if (!pop) return 'Various Locations';
    if (typeof pop === 'string') return pop || 'Various Locations';
    const city  = typeof pop.city  === 'object' ? pop.city?.name  : pop.city;
    const state = typeof pop.state === 'object' ? pop.state?.code : pop.state;
    const parts = [city, state].filter(Boolean);
    const base  = parts.join(', ');
    return (base + (pop.zip ? `  ${pop.zip}` : '')).trim() || 'Various Locations';
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const days = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div
      className="h-[calc(100vh-64px)] flex flex-col bg-white"
      data-testid="marketplace-sentinel"
      style={clientThemeVars}
    >

      {/* ── Compact single-row header ─────────────────────────────────────── */}
      <div
        className="flex-shrink-0 px-5 py-2.5 border-b flex items-center gap-3"
        style={{ borderColor: activeBrandColors ? 'var(--client-primary)' : '#e2e8f0' }}
      >
        {/* Brand */}
        <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Radar className="w-4 h-4 text-teal-600" />
        </div>
        <span className="text-sm font-semibold text-slate-900 tracking-tight whitespace-nowrap">Marketplace Sentinel</span>

        {/* Feed toggle */}
        <div className="flex gap-0.5 bg-slate-100 p-0.5 rounded-lg border border-slate-200 ml-1">
          <button
            onClick={() => setActiveFeed('sam')}
            className={cn('px-3 py-1 text-xs font-medium rounded flex items-center gap-1.5 transition-colors',
              activeFeed === 'sam' ? 'text-white shadow-sm' : 'text-slate-400 hover:text-slate-900')}
            style={activeFeed === 'sam' ? { backgroundColor: activeBrandColors ? 'var(--client-primary, #0d9488)' : '#0d9488' } : {}}
            data-testid="sentinel-tab-sam"
          >
            <Radar className="w-3 h-3" /> SAM.gov
          </button>
          <button
            onClick={() => setActiveFeed('ebuy')}
            className={cn('px-3 py-1 text-xs font-medium rounded flex items-center gap-1.5 transition-colors',
              activeFeed === 'ebuy' ? 'text-white shadow-sm' : 'text-slate-400 hover:text-slate-900')}
            style={activeFeed === 'ebuy' ? { backgroundColor: activeBrandColors ? 'var(--client-primary, #0e7490)' : '#0e7490' } : {}}
            data-testid="sentinel-tab-ebuy"
          >
            <FileText className="w-3 h-3" /> GSA eBuy
          </button>
          <button
            onClick={() => setActiveFeed('highergov')}
            className={cn('px-3 py-1 text-xs font-medium rounded flex items-center gap-1.5 transition-colors',
              activeFeed === 'highergov' ? 'text-white shadow-sm' : 'text-slate-400 hover:text-slate-900')}
            style={activeFeed === 'highergov' ? { backgroundColor: activeBrandColors ? 'var(--client-primary, #6366f1)' : '#6366f1' } : {}}
            data-testid="sentinel-tab-highergov"
          >
            <Globe className="w-3 h-3" /> 3rd Party
          </button>
        </div>

        <div className="flex-1" />

        {/* Client picker — consultants only */}
        {isConsultant && (
          <Select value={selectedClientId || 'ALL'} onValueChange={(v) => setSelectedClientId(v === 'ALL' ? '' : v)}>
            <SelectTrigger className="h-8 w-[160px] bg-white border-slate-200 text-slate-900 text-xs" data-testid="sentinel-client-picker">
              <Building2 className="w-3 h-3 mr-1.5 text-slate-400 flex-shrink-0" />
              <SelectValue placeholder="All clients" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL" data-testid="sentinel-client-option-ALL">All my clients</SelectItem>
              {clientOptions.map(c => (
                <SelectItem key={c.id} value={c.id} data-testid={`sentinel-client-option-${c.id}`}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Super-admin seed button */}
        {user?.role === 'SUPER_ADMIN' && stats.total_active === 0 && activeFeed === 'sam' && (
          <Button variant="outline" size="sm"
            onClick={async () => {
              try {
                const res = await fetch(`${API_URL}/api/sentinel/seed-demo-data`, { method: 'POST', headers: { 'Authorization': `Bearer ${authToken}` } });
                if (res.ok) { toast.success('Demo data seeded!'); fetchOpportunities(); fetchStats(); }
                else toast.error('Failed to seed demo data');
              } catch { toast.error('Failed to seed demo data'); }
            }}
            className="h-8 border-amber-400 text-amber-600 hover:bg-amber-50 text-xs"
          >
            <Zap className="w-3 h-3 mr-1" /> Seed Demo
          </Button>
        )}

        {/* AI Rank button — SAM */}
        {activeFeed === 'sam' && (
          <Button
            onClick={handleRankSam}
            disabled={rankingSam || !( selectedClientId || user?.organization_id)}
            variant="outline"
            className="h-8 px-3 text-xs border-purple-300 text-purple-700 hover:bg-purple-50 gap-1.5"
            title="AI-score each open opportunity against this client's portfolio"
            data-testid="ai-rank-sam-btn"
          >
            {rankingSam
              ? <><Loader2 className="w-3 h-3 animate-spin" />Ranking…</>
              : <><Brain className="w-3 h-3" />AI Rank</>}
          </Button>
        )}

        {/* Purge old unclaimed opps — inline popover */}
        {!needsClient && (
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const feed = activeFeed; // 'sam' | 'ebuy' | 'highergov'
                setShowPurgeDialog(showPurgeDialog === feed ? false : feed);
                setPurgeDays(30);
              }}
              className="h-8 px-2.5 text-xs border-red-200 text-red-600 hover:bg-red-50"
              title="Delete old unclaimed opportunities"
              data-testid="purge-old-opps-btn"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
            {showPurgeDialog && showPurgeDialog === activeFeed && (
              <div className="absolute right-0 top-10 z-50 w-64 bg-white border border-red-200 rounded-lg shadow-lg p-3 text-xs"
                   data-testid="purge-dialog">
                <p className="font-medium text-slate-800 mb-2">
                  Delete unclaimed {showPurgeDialog === 'sam' ? 'SAM' : showPurgeDialog === 'ebuy' ? 'eBuy' : 'HigherGov'} opportunities older than:
                </p>
                <div className="flex items-center gap-2 mb-3">
                  <Input
                    type="number"
                    min={1}
                    max={3650}
                    value={purgeDays}
                    onChange={e => setPurgeDays(Math.max(1, parseInt(e.target.value) || 1))}
                    className="h-7 text-xs w-20"
                  />
                  <span className="text-slate-600">days</span>
                </div>
                <p className="text-slate-500 mb-3">
                  Claimed opportunities are never deleted.
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handlePurge}
                    disabled={purging}
                    className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white flex-1"
                    data-testid="purge-confirm-btn"
                  >
                    {purging ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Deleting…</> : 'Delete'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowPurgeDialog(false)}
                    className="h-7 text-xs"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Primary action — consultants must pick a client first */}
        {activeFeed === 'sam' ? (
          <Button onClick={handleScan} disabled={scanning || needsClient}
            title={needsClient ? 'Select a client organization first' : undefined}
            className="bg-teal-500 hover:bg-teal-600 text-white h-8 px-4 text-xs" data-testid="scan-now-btn">
            {scanning
              ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Scanning…</>
              : <><RefreshCw className="w-3 h-3 mr-1.5" />Run Scan</>}
          </Button>
        ) : activeFeed === 'ebuy' ? (
          <Button onClick={() => ebuyTabRef.current?.triggerSync()} disabled={ebuySyncing || needsClient}
            title={needsClient ? 'Select a client organization first' : undefined}
            className="bg-cyan-600 hover:bg-cyan-700 text-white h-8 px-4 text-xs" data-testid="ebuy-sync-header-btn">
            {ebuySyncing
              ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Syncing…</>
              : <><FolderUp className="w-3 h-3 mr-1.5" />Sync eBuy folder</>}
          </Button>
        ) : (
          <Button onClick={() => hgTabRef.current?.triggerSync()} disabled={hgSyncing || needsClient}
            title={needsClient ? 'Select a client organization first' : undefined}
            className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 px-4 text-xs" data-testid="hg-sync-header-btn">
            {hgSyncing
              ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Syncing…</>
              : <><Globe className="w-3 h-3 mr-1.5" />Sync HigherGov</>}
          </Button>
        )}

        {/* Add by URL — SAM only, for opps the scan's filters missed (e.g. Special Notices) */}
        {activeFeed === 'sam' && !needsClient && (
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowManualAddDialog(v => !v)}
              className="h-8 px-2.5 text-xs border-teal-200 text-teal-700 hover:bg-teal-50"
              title="Add a specific SAM.gov opportunity by URL — bypasses the scan's filters"
              data-testid="manual-add-opp-btn"
            >
              <LinkIcon className="w-3.5 h-3.5" />
            </Button>
            {showManualAddDialog && (
              <div className="absolute right-0 top-10 z-50 w-80 bg-white border border-teal-200 rounded-lg shadow-lg p-3 text-xs"
                   data-testid="manual-add-dialog">
                <p className="font-medium text-slate-800 mb-1">Add a SAM.gov opportunity by URL</p>
                <p className="text-slate-500 mb-2">
                  For opportunities the scan didn't pick up — e.g. Special Notices (Industry Day, etc.)
                  or anything outside your NAICS targeting.
                </p>
                <Input
                  autoFocus
                  placeholder="https://sam.gov/workspace/contract/opp/.../view"
                  value={manualAddInput}
                  onChange={e => setManualAddInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleManualAdd(); }}
                  className="h-7 text-xs mb-3"
                  data-testid="manual-add-input"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleManualAdd}
                    disabled={manualAdding || !manualAddInput.trim()}
                    className="h-7 text-xs bg-teal-600 hover:bg-teal-700 text-white flex-1"
                    data-testid="manual-add-confirm-btn"
                  >
                    {manualAdding ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Adding…</> : 'Add'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setShowManualAddDialog(false); setManualAddInput(''); }}
                    className="h-7 text-xs"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── SAM stats strip ───────────────────────────────────────────────── */}
      {activeFeed === 'sam' && !needsClient && (
        <div className="flex-shrink-0 px-5 py-1.5 border-b border-slate-100 flex items-center gap-1.5 text-xs">
          <StatPill value={stats.suggested  || 0} label="New"       className="text-blue-600" />
          <span className="text-slate-700 px-1">·</span>
          <StatPill value={stats.gold_matches   || 0} label="Gold"   icon={Trophy}       className="text-amber-600" />
          <StatPill value={stats.silver_matches || 0} label="Silver" icon={Medal}         className="text-slate-600" />
          <StatPill value={stats.bronze_matches || 0} label="Bronze" icon={Award}         className="text-orange-600" />
          <span className="text-slate-700 px-1">·</span>
          <StatPill value={stats.claimed   || 0} label="Claimed"   icon={CheckCircle2} className="text-green-600" />
          <StatPill value={stats.dismissed || 0} label="Dismissed" icon={OctagonX}      className="text-slate-600" />
          {stats.last_scan && (
            <span className="ml-auto text-slate-400 text-[10px]">Last scan {formatDate(stats.last_scan)}</span>
          )}
        </div>
      )}

      {/* eBuy last-sync pill */}
      {activeFeed === 'ebuy' && ebuyLatestSync && !needsClient && (
        <div className="flex-shrink-0 px-5 py-1.5 border-b border-slate-100 flex items-center text-xs text-slate-500" data-testid="ebuy-last-sync-pill">
          Last sync: {new Date(ebuyLatestSync.synced_at).toLocaleString()}
          {ebuyLatestSync.opportunities_total !== undefined && <span className="ml-2">· {ebuyLatestSync.opportunities_total} rows</span>}
        </div>
      )}

      {/* HigherGov stats strip */}
      {activeFeed === 'highergov' && !needsClient && (
        <div className="flex-shrink-0 px-5 py-1.5 border-b border-slate-100 flex items-center gap-1.5 text-xs">
          <StatPill value={hgStats.suggested  || 0} label="New"       className="text-blue-600" />
          <span className="text-slate-700 px-1">·</span>
          <StatPill value={hgStats.gold_matches   || 0} label="Gold"   icon={Trophy} className="text-amber-600" />
          <StatPill value={hgStats.bronze_matches || 0} label="Bronze" icon={Award}  className="text-orange-600" />
          <span className="text-slate-700 px-1">·</span>
          <StatPill value={hgStats.claimed   || 0} label="Claimed"   icon={CheckCircle2} className="text-green-600" />
          <StatPill value={hgStats.dismissed || 0} label="Dismissed" icon={OctagonX}      className="text-slate-600" />
          {hgStats.last_sync && (
            <span className="ml-auto text-slate-400 text-[10px]">Last sync {formatDate(hgStats.last_sync)}</span>
          )}
        </div>
      )}

      {/* ── SAM error / expiry banners ────────────────────────────────────── */}
      {activeFeed === 'sam' && integrationStatus?.has_api_key && integrationStatus?.last_scan_result?.error && (
        <div className="flex-shrink-0 mx-5 mt-2 p-2.5 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-2" data-testid="sentinel-scan-error-banner">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-red-500" />
          <span>
            <strong className="text-red-800">Last scan failed.</strong>{' '}
            {integrationStatus.last_scan_result.error.detail || 'Unknown error.'}
            {integrationStatus.last_scan_result.error.status_code === 401 && ' Update your key in Workspace Settings → Integrations.'}
          </span>
        </div>
      )}
      {activeFeed === 'sam' && integrationStatus?.key_expiry_warning && (
        <div className="flex-shrink-0 mx-5 mt-2 mb-1 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700 flex items-start gap-2" data-testid="sentinel-key-expiry-banner">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-500" />
          <span>
            <strong className="text-amber-800">SAM.gov API key expires in {integrationStatus.days_until_expiry} day{integrationStatus.days_until_expiry === 1 ? '' : 's'}.</strong>{' '}
            Generate a replacement at{' '}
            <a href="https://sam.gov/data-services" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">sam.gov/data-services</a>
            {' '}and update in Workspace Settings → Integrations.
          </span>
        </div>
      )}

      {/* Consultants must pick a client before any feed renders. */}
      {needsClient ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-10" data-testid="sentinel-select-client-gate">
          <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mb-4">
            <Building2 className="w-7 h-7 text-slate-400" />
          </div>
          <p className="text-slate-700 font-semibold text-sm">Select a client organization</p>
          <p className="text-slate-500 text-xs mt-1 max-w-xs">
            Choose a client from the picker above to view their SAM.gov and GSA eBuy opportunities.
          </p>
        </div>
      ) : activeFeed === 'sam' ? (
        <div className="flex-1 flex flex-col overflow-hidden">

          {/* Filter bar */}
          <div className="flex-shrink-0 px-5 py-2 border-b border-slate-100 flex items-center gap-2" data-testid="scan-params-bar">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              <Input
                placeholder="Search title, agency, solicitation…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 w-72 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 text-xs"
                data-testid="search-opportunities"
              />
            </div>
            <Select value={filterTier} onValueChange={setFilterTier}>
              <SelectTrigger className="h-8 w-28 bg-white border-slate-200 text-slate-900 text-xs" data-testid="filter-tier">
                <SelectValue placeholder="All Tiers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="gold">Gold</SelectItem>
                <SelectItem value="silver">Silver</SelectItem>
                <SelectItem value="bronze">Bronze</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterAiRank} onValueChange={setFilterAiRank}>
              <SelectTrigger className="h-8 w-32 bg-white border-slate-200 text-slate-900 text-xs" data-testid="filter-ai-rank">
                <SelectValue placeholder="AI Fit" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All AI Fit</SelectItem>
                {Object.entries(AI_RANK_CONFIG).map(([key, cfg]) => (
                  <SelectItem key={key} value={key}>
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                      {cfg.label}
                    </span>
                  </SelectItem>
                ))}
                <SelectItem value="unranked">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full border border-slate-300" />
                    Unranked
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-8 w-28 bg-white border-slate-200 text-slate-900 text-xs" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="suggested">New</SelectItem>
                <SelectItem value="claimed">Claimed</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex-1" />
            <span className="text-[11px] text-slate-400">{filteredOpportunities.length} result{filteredOpportunities.length !== 1 ? 's' : ''}</span>
            <Button variant="outline" size="sm"
              onClick={() => setShowScanOptions(s => !s)}
              className={cn('h-8 border-slate-200 text-slate-600 hover:text-slate-900 text-xs gap-1.5', showScanOptions && 'bg-slate-100 text-slate-900 border-slate-300')}
            >
              <SlidersHorizontal className="w-3 h-3" />
              Scan Options
            </Button>
            <Button variant="outline" size="sm"
              onClick={() => setShowRankBands(s => !s)}
              className={cn('h-8 border-slate-200 text-slate-600 hover:text-slate-900 text-xs gap-1.5', showRankBands && 'bg-slate-100 text-slate-900 border-slate-300')}
              data-testid="ai-fit-bands-toggle"
            >
              <Brain className="w-3 h-3" />
              AI Fit Bands
            </Button>
          </div>

          {/* Per-org AI Fit thresholds — re-bands from stored scores, no AI cost */}
          {showRankBands && (
            <div className="flex-shrink-0 px-5 py-4 bg-slate-50 border-b border-slate-200" data-testid="ai-fit-bands-panel">
              <AiRankThresholds organizationId={selectedClientId || null} />
            </div>
          )}

          {/* Collapsible scan parameters */}
          {showScanOptions && (
            <div className="flex-shrink-0 px-5 py-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-end gap-4" data-testid="scan-params-expanded">
              {/* Response deadline */}
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-slate-600 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Response Deadline
                </Label>
                <div className="flex items-center gap-1">
                  <input type="date" value={scanRdlFrom} onChange={e => setScanRdlFrom(e.target.value)}
                    className="h-8 bg-white border border-slate-200 text-slate-900 text-xs rounded-md px-2 focus:outline-none focus:ring-1 focus:ring-teal-500"
                    data-testid="scan-rdl-from" />
                  <span className="text-slate-500 text-xs">→</span>
                  <input type="date" value={scanRdlTo} onChange={e => setScanRdlTo(e.target.value)}
                    className="h-8 bg-white border border-slate-200 text-slate-900 text-xs rounded-md px-2 focus:outline-none focus:ring-1 focus:ring-teal-500"
                    data-testid="scan-rdl-to" />
                </div>
              </div>

              {/* NAICS */}
              <div className="flex flex-col gap-1 min-w-[200px] max-w-[280px]">
                <Label className="text-xs text-slate-600 flex items-center gap-1">
                  <Search className="w-3 h-3" /> NAICS
                  {scanNaics.length === 0 && <span className="text-slate-400">(All)</span>}
                </Label>
                <div className="flex gap-1">
                  <Input value={naicsInput} onChange={e => setNaicsInput(e.target.value.toUpperCase())}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const v = naicsInput.trim(); if (v && !scanNaics.includes(v)) setScanNaics([...scanNaics, v]); setNaicsInput(''); } }}
                    placeholder="e.g. 541611"
                    className="h-8 bg-white border-slate-200 text-slate-900 text-xs placeholder:text-slate-400"
                    data-testid="scan-naics-input" />
                  <Button type="button" size="icon" variant="outline"
                    onClick={() => { const v = naicsInput.trim(); if (v && !scanNaics.includes(v)) setScanNaics([...scanNaics, v]); setNaicsInput(''); }}
                    className="h-8 w-8 border-slate-200 text-slate-400 hover:bg-slate-100 flex-shrink-0">
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
                {scanNaics.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {scanNaics.map(n => (
                      <Badge key={n} onClick={() => setScanNaics(scanNaics.filter(x => x !== n))}
                        className="bg-teal-100 text-teal-700 border border-teal-200 text-[10px] px-1.5 py-0 cursor-pointer hover:bg-red-100 hover:text-red-600 hover:border-red-200" title="Click to remove">
                        {n} <X className="w-2.5 h-2.5 ml-0.5 inline" />
                      </Badge>
                    ))}
                    <button type="button" onClick={() => setScanNaics([])} className="text-[10px] text-slate-500 hover:text-slate-400 underline">clear</button>
                  </div>
                )}
              </div>

              {/* Set-aside */}
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-slate-600 flex items-center gap-1">
                  <SlidersHorizontal className="w-3 h-3" /> Set-Aside
                  {scanSetAsides.length === 0 && <span className="text-slate-400">(All)</span>}
                </Label>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {SET_ASIDE_OPTIONS.map(({ code, label }) => {
                    const checked = scanSetAsides.includes(code);
                    return (
                      <label key={code} className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-400 hover:text-slate-900 select-none">
                        <input type="checkbox" checked={checked}
                          onChange={() => setScanSetAsides(checked ? scanSetAsides.filter(c => c !== code) : [...scanSetAsides, code])}
                          className="w-3 h-3 rounded accent-teal-500" data-testid={`scan-setaside-${code}`} />
                        {label}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Table + detail pane side-by-side */}
          <div className="flex-1 flex overflow-hidden">

            {/* Left column: scrollable table + persistent footer */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="w-6 h-6 text-teal-600 animate-spin" />
                  </div>
                ) : filteredOpportunities.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-center p-6">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                      <Radar className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-slate-400 font-medium text-sm">No opportunities found</p>
                    <p className="text-slate-500 text-xs mt-1">
                      {filterStatus === 'suggested' ? 'Click Run Scan to find new matches' : 'Try adjusting your filters'}
                    </p>
                  </div>
                ) : (
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="text-[11px] font-medium text-slate-500 uppercase tracking-wider border-b border-slate-200 sticky top-0 bg-white z-10">
                        {[
                          { label: 'AI Fit',   field: 'ai_rank',           cls: 'w-[72px] text-left' },
                          { label: 'Deadline', field: 'response_deadline', cls: 'w-[115px] text-left' },
                          { label: 'Tier',     field: 'match_tier',        cls: 'w-[88px] text-left' },
                          { label: 'Title · Agency', field: 'title',       cls: 'text-left' },
                          { label: 'NAICS',    field: 'naics_code',        cls: 'w-[70px] text-left' },
                          { label: 'Set-Aside', field: null,               cls: 'w-[136px] text-left' },
                          { label: 'Value',    field: 'estimated_value',   cls: 'w-[88px] text-right' },
                        ].map(({ label, field, cls }) => (
                          <th key={label}
                            className={cn('px-4 py-2.5', cls, field && 'cursor-pointer select-none hover:text-slate-700')}
                            onClick={field ? () => toggleSamSort(field) : undefined}
                          >
                            <span className="inline-flex items-center">
                              {label}{field && <SamSortIcon field={field} />}
                            </span>
                          </th>
                        ))}
                        <th className="w-8" />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOpportunities.map(opp => (
                        <OpportunityTableRow
                          key={opp.id}
                          opportunity={opp}
                          isSelected={selectedOpp?.id === opp.id}
                          onSelect={() => setSelectedOpp(prev => prev?.id === opp.id ? null : opp)}
                          formatCurrency={formatCurrency}
                          getDaysUntil={getDaysUntil}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Persistent pagination footer — always visible, never buried in scroll */}
              {!loading && samTotal > 0 && (
                <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-t border-slate-200 bg-white">
                  <span className="text-xs text-slate-500">
                    Showing <span className="font-medium text-slate-700">{opportunities.length}</span> of <span className="font-medium text-slate-700">{samTotal}</span> opportunities
                  </span>
                  {opportunities.length < samTotal && (
                    <Button variant="outline" size="sm" onClick={loadMoreSam} disabled={samLoadingMore}
                      className="h-7 text-xs gap-1.5" data-testid="sam-load-more">
                      {samLoadingMore ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3" />}
                      {samLoadingMore ? 'Loading…' : 'Load more'}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Detail pane — 460 px fixed, shown on row click */}
            {selectedOpp && (
              <div className="w-[460px] flex-shrink-0 border-l border-slate-200 overflow-y-auto" data-testid="opportunity-detail-pane">
                <OpportunityDetail
                  opportunity={selectedOpp}
                  onClaim={handleClaim}
                  onDismiss={handleDismiss}
                  onClose={() => setSelectedOpp(null)}
                  formatCurrency={formatCurrency}
                  formatDate={formatDate}
                  getDaysUntil={getDaysUntil}
                  formatLocation={formatLocation}
                  brandColors={activeBrandColors}
                />
              </div>
            )}
          </div>
        </div>

      ) : activeFeed === 'ebuy' ? (
        /* ── eBuy tab ────────────────────────────────────────────────────── */
        <div className="flex-1 flex overflow-hidden" data-testid="sentinel-ebuy-pane">
          <EbuyTab
            ref={ebuyTabRef}
            authToken={authToken}
            user={user}
            isConsultant={isConsultant}
            selectedClientId={selectedClientId}
            clientOptions={clientOptions}
            clientBrandColors={activeBrandColors}
            onSyncStateChange={setEbuySyncing}
            onLatestSyncChange={setEbuyLatestSync}
          />
        </div>
      ) : (
        /* ── 3rd Party (HigherGov) tab ───────────────────────────────────── */
        <div className="flex-1 flex overflow-hidden" data-testid="sentinel-highergov-pane">
          <HigherGovTab
            ref={hgTabRef}
            authToken={authToken}
            user={user}
            isConsultant={isConsultant}
            selectedClientId={selectedClientId}
            clientBrandColors={activeBrandColors}
            onSyncStateChange={setHgSyncing}
            onLatestSyncChange={(sync) => { setHgLatestSync(sync); fetchHgStats(); }}
          />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function StatPill({ value, label, icon: Icon, className }) {
  return (
    <span className={cn('flex items-center gap-1 font-medium whitespace-nowrap', className)}>
      {Icon && <Icon className="w-3 h-3" />}
      <span>{value}</span>
      <span className="text-slate-500 font-normal">{label}</span>
    </span>
  );
}

function OpportunityTableRow({ opportunity, isSelected, onSelect, formatCurrency, getDaysUntil }) {
  const tierConfig = MATCH_TIERS[opportunity.match_tier] || MATCH_TIERS.bronze;
  const TierIcon = tierConfig.icon;
  const daysUntil = getDaysUntil(opportunity.response_deadline);
  const isUrgent  = daysUntil !== null && daysUntil <= 7;
  const isWarning = daysUntil !== null && daysUntil > 7 && daysUntil <= 14;

  return (
    <tr
      onClick={onSelect}
      className={cn(
        'border-b border-slate-100 cursor-pointer transition-colors group',
        isSelected ? 'bg-slate-100' : 'hover:bg-slate-50',
      )}
      data-testid={`opp-row-${opportunity.id}`}
    >
      {/* AI Fit rank badge */}
      <td className={cn('px-4 py-3 w-[72px]', isSelected && 'border-l-2 border-l-teal-500')}>
        {opportunity.ai_rank ? (
          <Badge
            className={cn('text-[10px] font-semibold w-fit cursor-default', AI_RANK_CONFIG[opportunity.ai_rank]?.cls)}
            title={`AI Fit: ${opportunity.ai_rank}${opportunity.ai_rank_score != null ? ` (${opportunity.ai_rank_score}%)` : ''} — ${AI_RANK_CONFIG[opportunity.ai_rank]?.tip}${opportunity.ai_rank_reasoning ? '\n' + opportunity.ai_rank_reasoning : ''}`}
          >
            {AI_RANK_CONFIG[opportunity.ai_rank]?.label}
          </Badge>
        ) : (
          <span className="text-slate-300 text-xs" title="Not yet AI-ranked">—</span>
        )}
      </td>
      {/* Deadline */}
      <td className="px-4 py-3 w-[115px]">
        <div className={cn('font-medium text-xs tabular-nums',
          isUrgent ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-slate-600')}>
          {opportunity.response_deadline
            ? new Date(opportunity.response_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : '—'}
        </div>
        {daysUntil !== null && (
          <div className={cn('text-[10px] mt-0.5',
            isUrgent ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-slate-500')}>
            {daysUntil < 0 ? 'Closed' : daysUntil === 0 ? 'Today!' : `${daysUntil}d left`}
          </div>
        )}
      </td>
      {/* Tier badge */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 flex-wrap">
          <Badge className={cn('text-[10px] font-medium flex items-center gap-1 w-fit', tierConfig.badgeClass)}>
            <TierIcon className="w-3 h-3" />
            {tierConfig.label}
          </Badge>
          {opportunity.manually_added && (
            <Badge
              className="text-[10px] font-medium flex items-center gap-1 w-fit bg-teal-50 text-teal-700 border border-teal-200"
              title="Added manually by URL — bypassed the scan's automated filters"
            >
              <LinkIcon className="w-3 h-3" />
              Manual
            </Badge>
          )}
        </div>
      </td>
      {/* Title + Agency */}
      <td className="px-4 py-3 min-w-0">
        <div className="text-slate-900 font-medium text-sm leading-snug truncate" title={opportunity.title}>
          {opportunity.title}
        </div>
        <div className="text-slate-500 text-[11px] mt-0.5 flex items-center gap-2">
          <span className="truncate">{opportunity.agency || opportunity.department || '—'}</span>
          {opportunity.solicitation_number && (
            <span className="font-mono text-slate-400 flex-shrink-0">{opportunity.solicitation_number}</span>
          )}
        </div>
      </td>
      {/* NAICS */}
      <td className="px-4 py-3">
        <span className="font-mono text-xs text-slate-500">{opportunity.naics_code || '—'}</span>
      </td>
      {/* Set-aside */}
      <td className="px-4 py-3">
        {opportunity.set_aside ? (
          <Badge className="bg-purple-100 text-purple-700 border border-purple-200 text-[10px] font-normal max-w-[128px] truncate" title={opportunity.set_aside}>
            {opportunity.set_aside}
          </Badge>
        ) : (
          <span className="text-slate-700 text-xs">—</span>
        )}
      </td>
      {/* Value */}
      <td className="px-4 py-3 text-right">
        <span className="text-xs font-semibold text-teal-600">{formatCurrency(opportunity.estimated_value)}</span>
      </td>
      {/* Chevron */}
      <td className="px-2 py-3">
        <ChevronRight className={cn('w-4 h-4 transition-colors',
          isSelected ? 'text-teal-600' : 'text-slate-700 group-hover:text-slate-400')} />
      </td>
    </tr>
  );
}

// ── Detail pane ───────────────────────────────────────────────────────────────
function OpportunityDetail({ opportunity, onClaim, onDismiss, onClose, formatCurrency, formatDate, getDaysUntil, formatLocation, brandColors }) {
  const { authToken } = useAuth();
  const tierConfig = MATCH_TIERS[opportunity.match_tier] || MATCH_TIERS.bronze;
  const TierIcon = tierConfig.icon;
  const daysUntil = getDaysUntil(opportunity.response_deadline);
  const isClaimable = !opportunity.claimed_pursuit_id && opportunity.status !== 'dismissed';

  const [localLinks, setLocalLinks]           = useState(opportunity.resource_links || []);
  const [localAttachments, setLocalAttachments] = useState(opportunity.sam_attachments || []);
  const [fetchingDocs, setFetchingDocs]         = useState(false);
  const [fetchResult, setFetchResult]           = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLocalLinks(opportunity.resource_links || []);
    setLocalAttachments(opportunity.sam_attachments || []);
    setFetchResult(null);
  }, [opportunity.id]);

  const handleFetchDocs = async () => {
    setFetchingDocs(true);
    setFetchResult(null);
    try {
      const resp = await fetch(`${API_URL}/api/sentinel/opportunities/${opportunity.id}/fetch-docs`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await resp.json();
      if (!resp.ok) { toast.error(data.detail || 'Failed to fetch documents from SAM.gov'); return; }
      if (data.error) {
        toast.warning(`SAM.gov fetch: ${data.error}`);
      } else {
        const total = (data.links_found || 0) + (data.files_found || 0);
        total > 0
          ? toast.success(`Fetched ${total} item${total !== 1 ? 's' : ''} from SAM.gov`)
          : toast.info('No attachments or links found on this SAM.gov page');
      }
      if (Array.isArray(data.resource_links))  setLocalLinks(data.resource_links);
      if (Array.isArray(data.sam_attachments)) setLocalAttachments(data.sam_attachments);
      setFetchResult(data);
    } catch {
      toast.error('Network error while fetching SAM.gov documents');
    } finally {
      setFetchingDocs(false);
    }
  };

  const handleAttachmentDownload = async (att) => {
    try {
      const res = await fetch(`${API_URL}/api/sentinel/attachments/${att.gridfs_id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = att.file_name || 'attachment';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download attachment.');
    }
  };

  return (
    <div className="p-5" data-testid="opportunity-detail">

      {/* Close bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0', tierConfig.iconBg)}>
            <TierIcon className="w-3.5 h-3.5" />
          </div>
          <Badge className={cn('text-[10px]', tierConfig.badgeClass)}>
            {tierConfig.label} Match · {opportunity.match_score}%
          </Badge>
          {opportunity.manually_added && (
            <Badge
              className="text-[10px] font-medium flex items-center gap-1 bg-teal-50 text-teal-700 border border-teal-200"
              title="Added manually by URL — bypassed the scan's automated filters"
            >
              <LinkIcon className="w-3 h-3" />
              Manually Added
            </Badge>
          )}
          {opportunity.ai_rank && (
            <Badge
              className={cn('text-[10px] font-semibold', AI_RANK_CONFIG[opportunity.ai_rank]?.cls)}
              title={opportunity.ai_rank_reasoning || AI_RANK_CONFIG[opportunity.ai_rank]?.tip}
            >
              <Brain className="w-2.5 h-2.5 mr-0.5" />
              {AI_RANK_CONFIG[opportunity.ai_rank]?.label}
              {opportunity.ai_rank_score != null && ` · ${opportunity.ai_rank_score}%`}
            </Badge>
          )}
          {opportunity.status !== 'suggested' && (
            <Badge className={cn('text-[10px]', STATUS_CONFIG[opportunity.status]?.color)}>
              {STATUS_CONFIG[opportunity.status]?.label}
            </Badge>
          )}
        </div>
        <button onClick={onClose}
          className="w-7 h-7 rounded-md flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100/60 transition-colors"
          data-testid="close-detail-btn">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Title */}
      <h2 className="text-base font-semibold text-slate-900 leading-snug mb-1">{opportunity.title}</h2>
      <p className="text-teal-600 text-xs mb-1">{opportunity.match_reason}</p>
      {opportunity.ai_rank_reasoning && (
        <p className="text-purple-700 text-xs mb-3 flex items-start gap-1.5">
          <Brain className="w-3 h-3 mt-0.5 flex-shrink-0 text-purple-500" />
          {opportunity.ai_rank_reasoning}
        </p>
      )}
      {!opportunity.ai_rank_reasoning && <div className="mb-3" />}

      {/* Value + Deadline */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
          <div className="flex items-center gap-1.5 text-slate-400 text-[10px] mb-1"><DollarSign className="w-3 h-3" /> Estimated Value</div>
          <div className="text-lg font-bold text-teal-600">{formatCurrency(opportunity.estimated_value)}</div>
        </div>
        <div className={cn('rounded-lg p-3 border',
          daysUntil !== null && daysUntil <= 7  ? 'bg-red-50 border-red-200'
          : daysUntil !== null && daysUntil <= 14 ? 'bg-amber-50 border-amber-200'
          : 'bg-slate-50 border-slate-200')}>
          <div className="flex items-center gap-1.5 text-slate-400 text-[10px] mb-1"><Clock className="w-3 h-3" /> Response Deadline</div>
          <div className={cn('text-sm font-bold',
            daysUntil !== null && daysUntil <= 7 ? 'text-red-600'
            : daysUntil !== null && daysUntil <= 14 ? 'text-amber-600' : 'text-slate-700')}>
            {formatDate(opportunity.response_deadline)}
            {daysUntil !== null && <span className="text-xs font-normal ml-1 opacity-70">({daysUntil}d)</span>}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      {isClaimable && (
        <div className="flex gap-2 mb-4">
          <Button onClick={() => onClaim(opportunity.id)}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm h-9"
            data-testid="claim-pursuit-btn">
            <CircleCheck className="w-4 h-4 mr-1.5" /> Claim Pursuit
          </Button>
          <Button onClick={() => onDismiss(opportunity.id)}
            className="bg-red-600 hover:bg-red-700 text-white h-9 px-4"
            data-testid="dismiss-btn">
            <OctagonX className="w-4 h-4 mr-1.5" /> Dismiss
          </Button>
        </div>
      )}

      {/* Details grid */}
      <div className="bg-slate-50 rounded-lg border border-slate-200 mb-3">
        <div className="px-3.5 py-2.5 border-b border-slate-200 text-xs font-medium text-slate-600 flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5" /> Opportunity Details
        </div>
        <div className="p-3.5 grid grid-cols-2 gap-3">
          <DetailRow label="Solicitation #" value={opportunity.solicitation_number} mono />
          <DetailRow label="Posted Date"    value={formatDate(opportunity.posted_date)} />
          <DetailRow label="NAICS Code"     value={opportunity.naics_code} mono />
          <DetailRow label="PSC Code"       value={opportunity.psc_code || 'N/A'} mono />
        </div>
      </div>

      {/* Agency */}
      <div className="bg-slate-50 rounded-lg border border-slate-200 mb-3">
        <div className="px-3.5 py-2.5 border-b border-slate-200 text-xs font-medium text-slate-600 flex items-center gap-1.5">
          <Building2 className="w-3.5 h-3.5" /> Agency Information
        </div>
        <div className="p-3.5 space-y-2.5">
          <DetailRow label="Department"      value={opportunity.department} />
          <DetailRow label="Agency / Office" value={[opportunity.agency, opportunity.office].filter(Boolean).join(' / ') || 'N/A'} />
          <div className="flex items-center gap-2 text-xs">
            <MapPin className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
            <span className="text-slate-400">{formatLocation ? formatLocation(opportunity.place_of_performance) : (opportunity.place_of_performance || 'Various Locations')}</span>
          </div>
          {opportunity.set_aside && (
            <div className="flex items-center gap-2 pt-0.5">
              <span className="text-[10px] text-slate-500">Set-Aside</span>
              <Badge className="bg-purple-100 text-purple-700 border border-purple-200 text-[10px]">{opportunity.set_aside}</Badge>
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      {opportunity.description && (
        <div className="bg-slate-50 rounded-lg border border-slate-200 mb-3">
          <div className="px-3.5 py-2.5 border-b border-slate-200 text-xs font-medium text-slate-600">Description</div>
          <div className="p-3.5">
            <p className="text-xs text-slate-700 leading-relaxed">{opportunity.description}</p>
          </div>
        </div>
      )}

      {/* Point of contact */}
      {opportunity.point_of_contact && (
        <div className="bg-slate-50 rounded-lg border border-slate-200 mb-3">
          <div className="px-3.5 py-2.5 border-b border-slate-200 text-xs font-medium text-slate-600 flex items-center gap-1.5">
            <User className="w-3.5 h-3.5" /> Point of Contact
          </div>
          <div className="p-3.5">
            <div className="text-xs text-slate-900 font-medium">{opportunity.point_of_contact.name}</div>
            {opportunity.point_of_contact.email && (
              <a href={`mailto:${opportunity.point_of_contact.email}`} className="text-xs text-teal-600 hover:underline">
                {opportunity.point_of_contact.email}
              </a>
            )}
          </div>
        </div>
      )}

      {/* Attachments / links */}
      {localLinks.length > 0 && (
        <div className="bg-slate-50 rounded-lg border border-slate-200 mb-3">
          <div className="px-3.5 py-2.5 border-b border-slate-200 text-xs font-medium text-slate-600 flex items-center gap-1.5">
            <ExternalLink className="w-3.5 h-3.5" /> Attachments / Links
          </div>
          <div className="p-3 space-y-1">
            {localLinks.map((lnk, i) => (
              <a key={i} href={lnk.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs text-teal-600 hover:text-teal-600 hover:underline truncate py-0.5" title={lnk.url}>
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{lnk.description || lnk.url}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Downloaded attachments (stored in GridFS via Fetch Attachments) */}
      {localAttachments.length > 0 && (
        <div className="bg-slate-50 rounded-lg border border-slate-200 mb-3">
          <div className="px-3.5 py-2.5 border-b border-slate-200 text-xs font-medium text-slate-600 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" /> Attachments ({localAttachments.length})
          </div>
          <div className="p-3 space-y-1">
            {localAttachments.map((att, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                <FileText className="w-3 h-3 flex-shrink-0 text-teal-600" />
                <button
                  type="button"
                  onClick={() => handleAttachmentDownload(att)}
                  className="truncate text-teal-600 hover:text-teal-700 hover:underline text-left flex-1 min-w-0"
                  title={att.file_name}
                >
                  {att.file_name}
                </button>
                {att.size_bytes > 0 && (
                  <span className="text-[10px] text-slate-400 flex-shrink-0">
                    {att.size_bytes > 1048576
                      ? `${(att.size_bytes / 1048576).toFixed(1)} MB`
                      : `${Math.round(att.size_bytes / 1024)} KB`}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SAM.gov actions */}
      {opportunity.sam_link && (
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 border-slate-200 text-slate-400 hover:text-slate-900 hover:bg-slate-100 h-9 text-xs"
              onClick={() => window.open(opportunity.sam_link, '_blank')}>
              <Globe className="w-3.5 h-3.5 mr-1.5" /> View on SAM.gov <ExternalLink className="w-3 h-3 ml-1.5" />
            </Button>
            <Button variant="outline"
              className="flex-1 border-teal-300 text-teal-600 hover:text-teal-700 hover:bg-teal-50 h-9 text-xs"
              onClick={handleFetchDocs} disabled={fetchingDocs}>
              {fetchingDocs ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FolderUp className="w-3.5 h-3.5 mr-1.5" />}
              {fetchingDocs ? 'Fetching…' : 'Fetch Attachments'}
            </Button>
          </div>
          {fetchResult && (
            <p className="text-[10px] text-center text-slate-500">
              {fetchResult.error
                ? `⚠ ${fetchResult.error.split('(')[0].trim()}`
                : (fetchResult.links_found || 0) + (fetchResult.files_found || 0) > 0
                  ? `↳ ${fetchResult.links_found || 0} link${fetchResult.links_found !== 1 ? 's' : ''}` +
                    (fetchResult.files_found ? `, ${fetchResult.files_found} file${fetchResult.files_found !== 1 ? 's' : ''}` : '') +
                    (fetchResult.pursuit_docs_added ? ` · ${fetchResult.pursuit_docs_added} added to pursuit KB` : '')
                  : '↳ No new attachments found'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] text-slate-500 mb-0.5">{label}</div>
      <div className={cn('text-xs text-slate-700', mono && 'font-mono')}>{value || '—'}</div>
    </div>
  );
}
